import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Helmet } from "react-helmet";
import { Shield, Eye, EyeOff, Lock, Mail, AlertTriangle, Home, FileQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

// Secret access key - change this to your own secret
const ACCESS_KEY = "sp-admin-2024";

export default function BackofficeLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [showAccessInput, setShowAccessInput] = useState(false);

  // Check if user has access key in URL or is already logged in
  useEffect(() => {
    const checkAccess = async () => {
      // Check URL parameter for access
      const urlAccess = searchParams.get("access");
      if (urlAccess === ACCESS_KEY) {
        setShowLoginForm(true);
      }

      // Check if user is already logged in with admin access
      try {
        const res = await axios.post(`${API}/backoffice/validate-access`, {}, { withCredentials: true });
        if (res.data.has_access) {
          navigate("/backoffice/dashboard");
          return;
        }
      } catch (error) {
        // Not logged in or no admin access
      } finally {
        setChecking(false);
      }
    };
    checkAccess();
  }, [navigate, searchParams]);

  const handleAccessCode = (e) => {
    e.preventDefault();
    if (accessCode === ACCESS_KEY) {
      setShowLoginForm(true);
      setShowAccessInput(false);
    } else {
      toast.error("Invalid access code");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    // CRITICAL: Clear any previous session data before new login
    // This prevents data leakage between different users
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('backoffice_user');
    sessionStorage.clear();

    try {
      const loginRes = await axios.post(`${API}/backoffice/login`, {
        email,
        password
      }, { withCredentials: true });

      if (loginRes.data.requires_2fa) {
        toast.info("2FA verification required");
        navigate("/backoffice/2fa", { state: { email } });
        return;
      }

      if (loginRes.data.success) {
        toast.success("Welcome back!");
        // Force a full page navigation to ensure React state is fresh
        window.location.href = "/backoffice/dashboard";
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  // Show 404-style page if no access
  if (!showLoginForm) {
    return (
      <>
        <Helmet>
          <title>404 - Page Not Found | SeatPilot</title>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="mb-8">
              <FileQuestion className="w-24 h-24 text-slate-600 mx-auto mb-4" />
              <h1 className="text-6xl font-bold text-white mb-2">404</h1>
              <h2 className="text-2xl font-semibold text-slate-300 mb-4">Page Not Found</h2>
              <p className="text-slate-400 mb-8">
                The page you're looking for doesn't exist or has been moved.
              </p>
            </div>

            <div className="space-y-4">
              <Button 
                onClick={() => navigate("/")}
                className="w-full bg-violet-600 hover:bg-violet-700"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Homepage
              </Button>

              {!showAccessInput ? (
                <button
                  onClick={() => setShowAccessInput(true)}
                  className="text-xs text-slate-600 hover:text-slate-500 transition-colors"
                >
                  Staff Access
                </button>
              ) : (
                <form onSubmit={handleAccessCode} className="mt-6 space-y-3">
                  <div className="flex gap-2">
                    <Input
                      type="password"
                      placeholder="Enter access code"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value)}
                      className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 text-sm"
                    />
                    <Button type="submit" size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                      <Lock className="w-4 h-4" />
                    </Button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAccessInput(false)}
                    className="text-xs text-slate-600 hover:text-slate-500"
                  >
                    Cancel
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Show actual login form if access granted
  return (
    <>
      <Helmet>
        <title>Admin Portal | SeatPilot</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-violet-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-violet-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Admin Portal</h1>
            <p className="text-slate-400 text-sm mt-2">Secure access for authorized personnel only</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@company.com"
                  className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Authenticating...
                </span>
              ) : (
                "Access Portal"
              )}
            </Button>
          </form>

          <div className="mt-6 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-200/80">
                All access attempts are logged and monitored.
              </p>
            </div>
          </div>

          <div className="mt-4 text-center">
            <button
              onClick={() => navigate("/")}
              className="text-xs text-slate-500 hover:text-slate-400"
            >
              ← Back to Homepage
            </button>
          </div>
        </Card>
      </div>
    </>
  );
}
